import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../hooks/redux';
import { clearFloorPlan, setFloorPlan } from '../../features/floorplan/floorplanSlice';
import { clearLayout } from '../../features/layout/layoutSlice';
import { clearFacility } from '../../features/facility/facilitySlice';
import { clearEquipment } from '../../features/equipment/equipmentSlice';
import { clearDxfImport } from '../../features/dxfImport/dxfImportSlice';
import { clearPointDraw, setActiveTool, setPointDrawActive, setZoomLevel } from '../../features/ui/uiSlice';
import { clearSelection } from '../../features/selection/selectionSlice';
import { DEFAULT_ZOOM } from '../../constants';
import { endAuthSession } from '../../utils/sessionLogout';
import { viewportStore } from '../../store/viewportStore';
import {
    BackendFloorPlanListItem,
    deleteBackendFloorPlan,
    fetchBackendFloorPlans,
    normalizeDateString,
    renameBackendFloorPlan,
} from '../../utils/dxfApi';
import { getFloorPlansApiBase, getImportsApiUrl } from '../../utils/apiConfig';
import { runDxfImport, runDxfOpenExisting } from '../../utils/startDxfImport';
import { store } from '../../store';
import BriteFloLogo from '../icons/BriteFloLogo';
import { PoweredByPathfinder } from '../common/PoweredByPathfinder';

const MAX_RECENT_PROJECTS = 10;
const MIN_SKELETON_MS = 350;

const thumbnailThemes = [
    {
        frame: 'border-blue-100 bg-blue-50',
        line: 'bg-blue-300',
        block: 'bg-blue-200',
        node: 'bg-blue-400',
    },
    {
        frame: 'border-violet-100 bg-violet-50',
        line: 'bg-violet-300',
        block: 'bg-violet-200',
        node: 'bg-violet-400',
    },
    {
        frame: 'border-cyan-100 bg-cyan-50',
        line: 'bg-cyan-300',
        block: 'bg-cyan-100',
        node: 'bg-cyan-400',
    },
    {
        frame: 'border-emerald-100 bg-emerald-50',
        line: 'bg-emerald-300',
        block: 'bg-emerald-100',
        node: 'bg-emerald-400',
    },
] as const;

function formatRelativeDate(value: string | null): string {
    if (!value) return 'an unknown date';
    const normalized = normalizeDateString(value);
    const date = normalized ? new Date(normalized) : new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    const elapsedMs = Date.now() - date.getTime();
    if (elapsedMs < 0) return 'just now';
    const minuteMs = 60 * 1000;
    const hourMs = 60 * minuteMs;
    const dayMs = 24 * hourMs;

    if (elapsedMs < minuteMs) return 'just now';
    if (elapsedMs < hourMs) {
        const minutes = Math.max(1, Math.round(elapsedMs / minuteMs));
        return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    }
    if (elapsedMs < dayMs) {
        const hours = Math.max(1, Math.round(elapsedMs / hourMs));
        return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    }
    if (elapsedMs < dayMs * 2) return 'Yesterday';
    if (elapsedMs < dayMs * 7) {
        const days = Math.max(1, Math.round(elapsedMs / dayMs));
        return `${days} days ago`;
    }

    return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

function projectMeta(item: BackendFloorPlanListItem): string {
    const descriptor = item.category || item.layer || item.block || 'DXF project';
    const opened = item.opened_at
        ? `Last opened ${formatRelativeDate(item.opened_at)}`
        : 'Not opened yet';
    return `${descriptor} · ${opened}`;
}

/** Last-opened time, or creation time for plans never opened (matches the backend sort). */
function recencyTime(item: BackendFloorPlanListItem): number {
    const value = item.opened_at ?? item.created_at;
    if (!value) return 0;
    const normalized = normalizeDateString(value);
    const time = normalized ? Date.parse(normalized) : Date.parse(value);
    return Number.isNaN(time) ? 0 : time;
}

const FloorPlanLanding: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const uploadInputRef = useRef<HTMLInputElement>(null);
    const profileMenuRef = useRef<HTMLDivElement>(null);
    const mountedRef = useRef(false);
    const plansRequestRef = useRef(0);

    const [plans, setPlans] = useState<BackendFloorPlanListItem[]>([]);
    const [loadingPlans, setLoadingPlans] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);

    const [deletingFloorPlanId, setDeletingFloorPlanId] = useState<string | null>(null);
    const [renamingFloorPlanId, setRenamingFloorPlanId] = useState<string | null>(null);
    const [renameDraft, setRenameDraft] = useState('');
    const [savingRename, setSavingRename] = useState(false);
    const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);
    const [uploadProjectName, setUploadProjectName] = useState('');
    const [actionError, setActionError] = useState<string | null>(null);
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
    const [cardsRevealed, setCardsRevealed] = useState(false);

    const importsApiUrl = getImportsApiUrl();
    const floorPlansApiBase = getFloorPlansApiBase();

    const loadPlans = async () => {
        const requestId = ++plansRequestRef.current;
        const startedAt = Date.now();
        setLoadingPlans(true);
        setLoadingError(null);
        try {
            const items = await fetchBackendFloorPlans({ baseUrl: floorPlansApiBase });
            const sorted = [...items].sort((a, b) => recencyTime(b) - recencyTime(a));
            if (mountedRef.current && requestId === plansRequestRef.current) {
                setPlans(sorted);
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            if (mountedRef.current && requestId === plansRequestRef.current) {
                setLoadingError(message);
            }
        } finally {
            const remaining = MIN_SKELETON_MS - (Date.now() - startedAt);
            if (remaining > 0) {
                await new Promise((resolve) => setTimeout(resolve, remaining));
            }
            if (mountedRef.current && requestId === plansRequestRef.current) {
                setLoadingPlans(false);
            }
        }
    };

    useEffect(() => {
        mountedRef.current = true;
        void loadPlans();
        
        // Clear equipment state and local storage when landing page mounts
        dispatch(clearEquipment());
        try {
            localStorage.removeItem('layout_editor_equipment');
        } catch (e) {
            console.error('Failed to clear equipment local storage:', e);
        }

        return () => {
            mountedRef.current = false;
            plansRequestRef.current += 1;
        };
    }, []);

    const recentPlans = plans.slice(0, MAX_RECENT_PROJECTS);

    useEffect(() => {
        if (loadingPlans) {
            setCardsRevealed(false);
            return;
        }

        setCardsRevealed(false);
        const raf = requestAnimationFrame(() => {
            setCardsRevealed(true);
        });

        return () => cancelAnimationFrame(raf);
    }, [loadingPlans, recentPlans.length]);

    useEffect(() => {
        if (!profileMenuOpen) return;
        const handlePointerDown = (event: MouseEvent) => {
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
                setProfileMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [profileMenuOpen]);

    const openShellFloorPlan = (displayName: string) => {
        dispatch(clearLayout());
        dispatch(clearFacility());
        dispatch(clearEquipment());
        dispatch(clearSelection());
        dispatch(setPointDrawActive(false));
        dispatch(clearPointDraw());
        dispatch(setActiveTool('select'));
        dispatch(
            setFloorPlan({
                name: displayName,
                polylines: [],
                bounds: {
                    minX: 0,
                    minY: 0,
                    maxX: 2000,
                    maxY: 2000,
                },
                scaleFactor: 1,
                layers: [],
                polylineLayers: [],
                polylineMeta: [],
                labels: [],
            })
        );
        viewportStore.setState({ zoom: DEFAULT_ZOOM, panX: 0, panY: 0 });
        dispatch(setZoomLevel(DEFAULT_ZOOM));
    };

    const handleOpenExisting = (item: BackendFloorPlanListItem) => {
        setActionError(null);
        const nowIso = new Date().toISOString();
        setPlans((current) =>
            current.map((plan) =>
                plan.floor_plan_id === item.floor_plan_id
                    ? { ...plan, opened_at: nowIso }
                    : plan
            )
        );
        openShellFloorPlan(item.name);
        runDxfOpenExisting({
            dispatch,
            getState: store.getState,
            floorPlanId: item.floor_plan_id,
            projectName: item.name,
            floorPlansBaseUrl: floorPlansApiBase,
        });
    };

    const handleDeletePlan = async (item: BackendFloorPlanListItem) => {
        if (deletingFloorPlanId) return;
        if (!window.confirm(`Delete “${item.name}”? This cannot be undone.`)) return;

        setActionError(null);
        setDeletingFloorPlanId(item.floor_plan_id);
        try {
            await deleteBackendFloorPlan({
                floorPlanId: item.floor_plan_id,
                baseUrl: floorPlansApiBase,
            });
            setPlans((current) =>
                current.filter((plan) => plan.floor_plan_id !== item.floor_plan_id)
            );
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            setActionError(message);
        } finally {
            setDeletingFloorPlanId(null);
        }
    };

    const startRename = (item: BackendFloorPlanListItem) => {
        if (savingRename) return;
        setActionError(null);
        setRenamingFloorPlanId(item.floor_plan_id);
        setRenameDraft(item.name);
    };

    const cancelRename = () => {
        if (savingRename) return;
        setRenamingFloorPlanId(null);
        setRenameDraft('');
    };

    const submitRename = async (item: BackendFloorPlanListItem) => {
        if (savingRename) return;
        const nextName = renameDraft.trim();
        if (!nextName || nextName === item.name) {
            cancelRename();
            return;
        }

        setActionError(null);
        setSavingRename(true);
        try {
            const savedName = await renameBackendFloorPlan({
                floorPlanId: item.floor_plan_id,
                name: nextName,
                baseUrl: floorPlansApiBase,
            });
            setPlans((current) =>
                current.map((plan) =>
                    plan.floor_plan_id === item.floor_plan_id ? { ...plan, name: savedName } : plan
                )
            );
            setRenamingFloorPlanId(null);
            setRenameDraft('');
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            setActionError(message);
        } finally {
            setSavingRename(false);
        }
    };

    const handlePickUpload = () => {
        uploadInputRef.current?.click();
    };

    const prepareUpload = (file: File) => {
        const defaultName = file.name.replace(/\.(dxf|dwf)$/i, '').trim() || file.name;
        setActionError(null);
        setPendingUploadFile(file);
        setUploadProjectName(defaultName);
    };

    const cancelPendingUpload = () => {
        setPendingUploadFile(null);
        setUploadProjectName('');
        setActionError(null);
    };

    const handleOpenEditorDirect = () => {
        setActionError(null);
        openShellFloorPlan('Untitled Layout');
    };

    const uploadFile = (file: File, projectName: string) => {
        const displayName = projectName.trim();
        if (!file || !displayName) return;

        setActionError(null);
        setPendingUploadFile(null);
        setUploadProjectName('');

        openShellFloorPlan(displayName);

        runDxfImport({
            dispatch,
            getState: store.getState,
            file,
            projectName: displayName,
            importsUrl: importsApiUrl,
            floorPlansBaseUrl: floorPlansApiBase,
        });
    };

    const handleUploadInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] ?? null;
        event.target.value = '';
        if (file) prepareUpload(file);
    };

    const handleDrop = (event: React.DragEvent<HTMLButtonElement>) => {
        event.preventDefault();
        const file = event.dataTransfer.files?.[0] ?? null;
        if (file) prepareUpload(file);
    };

    const handleLogout = async () => {
        dispatch(clearFacility());
        dispatch(clearEquipment());
        dispatch(clearDxfImport());
        dispatch(clearFloorPlan());
        dispatch(clearLayout());
        try {
            localStorage.removeItem('layout_editor_equipment');
        } catch (e) {
            console.error('Failed to clear equipment local storage:', e);
        }
        // Revoke refresh cookie + erase access token so refresh shows sign-in.
        await endAuthSession();
    };

    return (
        <div className="flex min-h-screen flex-col overflow-hidden bg-[#eff4fa] text-slate-900">
            <header className="flex h-[42px] shrink-0 items-center justify-between border-b border-slate-200 bg-white px-5">
                <div className="flex items-center gap-1.5">
                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">
                      <BriteFloLogo />
                    </div>
                    <span className="text-[11px] font-semibold text-slate-900">Peakaso</span>
                </div>
                <div className="flex items-center gap-4 text-[11px] text-slate-500">
                    <span>v2.4.1</span>
                    <div ref={profileMenuRef} className="relative">
                        <button
                            type="button"
                            title="Account"
                            aria-label="Account"
                            onClick={() => setProfileMenuOpen((open) => !open)}
                            className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors ${
                                profileMenuOpen
                                    ? 'bg-slate-100 text-slate-700'
                                    : 'text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            <UserIcon />
                        </button>
                        {profileMenuOpen && (
                            <div className="absolute right-0 top-full z-50 mt-2 min-w-[150px] rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setLogoutConfirmOpen(false);
                                        setProfileMenuOpen(false);
                                        navigate('/profile');
                                    }}
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-100"
                                >
                                    <UserIcon />
                                    Profile
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setProfileMenuOpen(false);
                                        setLogoutConfirmOpen(true);
                                    }}
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-100"
                                >
                                    <LogoutIcon />
                                    Logout
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <main className="flex flex-1 overflow-hidden px-6 py-6 lg:px-16">
                <div className="grid w-full grid-cols-1 gap-8 self-start lg:grid-cols-[360px_minmax(0,1fr)] xl:gap-10">
                    <section className="pt-1 lg:pt-5">
                        <h1 className="max-w-[350px] text-[32px] font-extrabold leading-[1.08] tracking-[-0.04em] text-slate-950 md:text-[34px]">
                            Design your overhead{' '}
                            <span className="block text-blue-600">rail system</span>
                        </h1>
                        <p className="mt-4 max-w-[330px] text-[13px] leading-6 text-slate-500">
                            Import a DXF layout or open a saved project to get started immediately.
                        </p>

                        <div className="mt-8 w-full max-w-[360px] rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                            <div className="flex items-start gap-3">
                                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-lg font-medium text-blue-600">
                                    +
                                </div>
                                <div>
                                    <div className="text-[13px] font-bold text-slate-950">Import DXF</div>
                                    <div className="mt-0.5 text-[11px] text-slate-400">Load a .dxf/.dwf files only</div>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={handlePickUpload}
                                onDragOver={(event) => event.preventDefault()}
                                onDrop={(event) => void handleDrop(event)}
                                className="mt-5 flex h-[70px] w-full items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 text-center text-[11px] text-slate-500 transition-colors hover:border-blue-300 hover:bg-blue-50/40 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <span className={`mr-2 text-sm ${pendingUploadFile ? 'text-emerald-600' : 'text-slate-500'}`}>
                                    {pendingUploadFile ? '✓' : '⇧'}
                                </span>
                                {pendingUploadFile ? (
                                    <span className="min-w-0">
                                        <span className="block max-w-[230px] truncate font-semibold text-slate-700">
                                            {pendingUploadFile.name}
                                        </span>
                                        <span className="mt-0.5 block text-[10px] text-blue-600">Choose another file</span>
                                    </span>
                                ) : (
                                    <span>
                                        Drag &amp; drop your .DXF file here, or{' '}
                                        <span className="font-semibold text-blue-600">browse files</span>
                                    </span>
                                )}
                            </button>

                            <div className="mt-3">
                                <div className="flex items-center justify-between">
                                    <label htmlFor="landing-project-name" className="text-[11px] font-semibold text-slate-700">
                                        Floor plan name
                                    </label>
                                    {pendingUploadFile && (
                                        <button
                                            type="button"
                                            onClick={cancelPendingUpload}
                                            className="text-[10px] font-medium text-slate-400 hover:text-slate-600"
                                        >
                                            Clear file
                                        </button>
                                    )}
                                </div>
                                <input
                                    id="landing-project-name"
                                    type="text"
                                    value={uploadProjectName}
                                    onChange={(event) => setUploadProjectName(event.target.value)}
                                    maxLength={120}
                                    placeholder="Select a DXF file to name it"
                                    className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-[12px] text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                                />
                            </div>

                            <div className="mt-3 flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={handleOpenEditorDirect}
                                    className="rounded-lg border border-blue-500 bg-white px-4 py-2 text-[11px] font-semibold text-blue-600 transition-colors hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Open Project
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (pendingUploadFile) {
                                            uploadFile(pendingUploadFile, uploadProjectName);
                                        }
                                    }}
                                    disabled={!pendingUploadFile || !uploadProjectName.trim()}
                                    className="inline-flex min-w-[112px] items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-[11px] font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-blue-200"
                                >
                                    Upload
                                </button>
                            </div>
                        </div>

                        {(loadingError || actionError) && (
                            <div className="mt-4 max-w-[360px] rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-5 text-amber-800">
                                {loadingError && (
                                    <div>
                                        Failed to load floor plans: {loadingError}
                                        <button
                                            type="button"
                                            className="ml-2 font-semibold text-amber-900 underline"
                                            onClick={() => void loadPlans()}
                                        >
                                            Retry
                                        </button>
                                    </div>
                                )}
                                {actionError && <div>{actionError}</div>}
                            </div>
                        )}
                    </section>

                    <section className="min-h-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:max-h-[628px]">
                        <div className="mb-4 flex items-start justify-between gap-4 px-1">
                            <div>
                                <h2 className="text-[13px] font-bold text-slate-950">Continue where you left off</h2>
                                <p className="mt-1 text-[11px] text-slate-400">
                                    {loadingPlans ? 'Loading projects from the API…' : 'Recent projects, sorted by last opened'}
                                </p>
                            </div>
                            {loadingPlans ? (
                                <div
                                    className="flex items-center gap-2 rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-semibold text-blue-600"
                                    role="status"
                                    aria-live="polite"
                                >
                                    <LoadingSpinner />
                                    Loading
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    className="text-[11px] font-semibold text-blue-600 hover:text-blue-700"
                                >
                                    Table view
                                </button>
                            )}
                        </div>

                        <div
                            className="grid min-h-[364px] max-h-[532px] grid-cols-1 content-start gap-2 overflow-y-auto pr-1 sm:grid-cols-2"
                            aria-busy={loadingPlans}
                        >
                            {loadingPlans ? (
                                <ProjectGridSkeleton />
                            ) : recentPlans.length === 0 ? (
                                <div className="col-span-full flex min-h-[220px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 px-8 text-center text-sm text-slate-500">
                                    No saved DXF projects were found. Import a DXF to start a project.
                                </div>
                            ) : (
                                recentPlans.map((item, idx) => {
                                    const isDeleting = deletingFloorPlanId === item.floor_plan_id;
                                    const isRenaming = renamingFloorPlanId === item.floor_plan_id;
                                    const statusColor = idx % 4 === 3 ? 'bg-slate-500' : 'bg-emerald-500';
                                    return (
                                        <div
                                            key={item.floor_plan_id}
                                            className={`group relative h-[116px] rounded-lg border border-slate-200 bg-slate-50 transition-[opacity,transform,box-shadow,border-color,background-color] duration-500 ease-out hover:border-blue-200 hover:bg-white hover:shadow-sm ${cardsRevealed ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`}
                                            style={{ transitionDelay: `${Math.min(idx * 45, 320)}ms` }}
                                        >
                                            {isRenaming ? (
                                                <div className="flex h-full w-full items-center gap-3 rounded-lg p-3">
                                                    <div className="relative shrink-0">
                                                        <ProjectThumbnail index={idx} />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <input
                                                            type="text"
                                                            autoFocus
                                                            aria-label={`New name for ${item.name}`}
                                                            value={renameDraft}
                                                            maxLength={255}
                                                            disabled={savingRename}
                                                            onChange={(event) => setRenameDraft(event.target.value)}
                                                            onFocus={(event) => event.target.select()}
                                                            onKeyDown={(event) => {
                                                                if (event.key === 'Enter') void submitRename(item);
                                                                if (event.key === 'Escape') cancelRename();
                                                            }}
                                                            className="w-full rounded-md border border-blue-400 bg-white px-2 py-1 text-[13px] font-bold text-slate-900 outline-none focus:ring-2 focus:ring-blue-100 disabled:opacity-60"
                                                        />
                                                        <div className="mt-2 flex items-center gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => void submitRename(item)}
                                                                disabled={savingRename || !renameDraft.trim()}
                                                                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-2.5 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-200"
                                                            >
                                                                {savingRename && <LoadingSpinner />}
                                                                Save
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={cancelRename}
                                                                disabled={savingRename}
                                                                className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                            <>
                                            <button
                                                type="button"
                                                className="flex h-full w-full items-center gap-3 rounded-lg p-3 text-left focus:outline-none focus-visible:shadow-[inset_0_0_0_2px_rgba(59,130,246,0.55)] disabled:cursor-wait"
                                                onClick={() => handleOpenExisting(item)}
                                                disabled={!!deletingFloorPlanId}
                                            >
                                                <div className="relative shrink-0">
                                                    <ProjectThumbnail index={idx} />
                                                </div>
                                                <div className="min-w-0 flex-1 pr-14">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${statusColor}`} />
                                                        <span className="truncate text-[13px] font-bold text-slate-900">
                                                            {item.name}
                                                        </span>
                                                    </div>
                                                    <div className="mt-2 truncate text-[11px] text-slate-500">
                                                        {projectMeta(item)}
                                                    </div>
                                                </div>
                                            </button>
                                            <button
                                                type="button"
                                                aria-label={`Rename ${item.name}`}
                                                title={`Rename ${item.name}`}
                                                onClick={() => startRename(item)}
                                                disabled={!!deletingFloorPlanId || savingRename}
                                                className={`absolute right-10 top-2 flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:cursor-not-allowed ${
                                                    isDeleting ? 'hidden' : 'opacity-0 group-hover:opacity-100'
                                                }`}
                                            >
                                                <PencilIcon />
                                            </button>
                                            <button
                                                type="button"
                                                aria-label={`Delete ${item.name}`}
                                                title={`Delete ${item.name}`}
                                                onClick={() => void handleDeletePlan(item)}
                                                disabled={!!deletingFloorPlanId && !isDeleting}
                                                className={`absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md border border-red-100 bg-white text-red-500 shadow-sm transition hover:border-red-200 hover:bg-red-50 hover:text-red-700 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-red-300 disabled:cursor-wait ${
                                                    isDeleting
                                                        ? 'opacity-100'
                                                        : 'opacity-0 group-hover:opacity-100'
                                                }`}
                                            >
                                                {isDeleting ? <LoadingSpinner /> : <TrashIcon />}
                                            </button>
                                            </>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </section>
                </div>

                <input
                    ref={uploadInputRef}
                    type="file"
                    accept=".dxf,.dwf"
                    className="hidden"
                    onChange={handleUploadInputChange}
                />
            </main>

            <footer className="flex h-[44px] shrink-0 items-center justify-between border-t border-slate-200 px-6 text-[11px] text-slate-500 lg:px-6">
                <span>
                    <PoweredByPathfinder className="text-slate-600" />
                    {/* <span className="mx-2 text-slate-300">·</span> */}
                    {/* © 2026 Peakaso */}
                </span>
                <div className="flex items-center gap-5">
                    <a className="hover:text-slate-700" href="/docs/cad-normalization-guide.html">
                        Documentation
                    </a>
                    <button type="button" className="hover:text-slate-700">
                        Keyboard shortcuts
                    </button>
                    <button type="button" className="hover:text-slate-700">
                        Release notes
                    </button>
                </div>
            </footer>

            {logoutConfirmOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 backdrop-blur-sm"
                    onClick={() => setLogoutConfirmOpen(false)}
                >
                    <div
                        className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-5 shadow-2xl"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="flex items-start gap-3">
                            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                                <LogoutIcon />
                            </div>
                            <div>
                                <div className="text-base font-semibold text-slate-900">Log out?</div>
                                <div className="mt-1 text-sm leading-5 text-slate-500">
                                    This will end your session and return you to the login screen.
                                </div>
                            </div>
                        </div>

                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setLogoutConfirmOpen(false)}
                                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setLogoutConfirmOpen(false);
                                    handleLogout();
                                }}
                                className="rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-amber-700"
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

function LoadingSpinner({ className = 'h-3 w-3' }: { className?: string }) {
    return (
        <svg
            className={`${className} animate-spin`}
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
        >
            <circle className="opacity-25" cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" />
            <path
                className="opacity-90"
                d="M21 12a9 9 0 0 0-9-9"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
            />
        </svg>
    );
}

function TrashIcon() {
    return (
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M3.75 5.25h12.5M8 8.25v5.5m4-5.5v5.5M6 5.25l.6 10h6.8l.6-10M7.5 5.25l.5-1.5h4l.5 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function PencilIcon() {
    return (
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M13.5 3.75l2.75 2.75L7.5 15.25l-3.5.75.75-3.5L13.5 3.75z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function UserIcon() {
    return (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path
                d="M3.24023 3.72461C3.24023 2.01277 4.62797 0.625001 6.33984 0.625001C8.05168 0.625001 9.43941 2.01277 9.43941 3.72461C9.43941 5.43645 8.05168 6.82422 6.33984 6.82422C4.62797 6.82422 3.24023 5.43645 3.24023 3.72461Z"
                stroke="currentColor"
                strokeWidth="1.25"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M0.625 12.5391C0.625 9.38281 3.18363 6.82418 6.33988 6.82418C9.49613 6.82418 12.0548 9.38281 12.0548 12.5391"
                stroke="currentColor"
                strokeWidth="1.25"
                strokeMiterlimit="10"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function LogoutIcon() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M10 5H6.5A2.5 2.5 0 0 0 4 7.5v9A2.5 2.5 0 0 0 6.5 19H10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M15 8l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M19 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function ProjectGridSkeleton() {
    return (
        <>
            {Array.from({ length: 8 }).map((_, idx) => (
                <div
                    key={`skeleton-${idx}`}
                    data-testid="project-card-skeleton"
                    className="flex h-[116px] animate-pulse items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3"
                    aria-hidden="true"
                >
                    <div className="h-[76px] w-[112px] shrink-0 rounded-lg border border-slate-200 bg-slate-200/80">
                        <div className="mx-auto mt-[37px] h-1 w-16 rounded bg-slate-300/70" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-3">
                        <div className="h-3 w-4/5 rounded bg-slate-200" />
                        <div className="h-2.5 w-full rounded bg-slate-200/80" />
                        <div className="h-2.5 w-2/3 rounded bg-slate-200/70" />
                    </div>
                </div>
            ))}
        </>
    );
}

function ProjectThumbnail({ index }: { index: number }) {
    const theme = thumbnailThemes[index % thumbnailThemes.length];

    return (
        <div className={`relative h-[76px] w-[112px] shrink-0 overflow-hidden rounded-lg border ${theme.frame}`}>
            <div className={`absolute left-5 right-5 top-1/2 h-px ${theme.line}`} />
            <div className={`absolute left-6 top-6 h-5 w-8 rounded-sm border ${theme.line} bg-white/30`} />
            <div className={`absolute right-6 top-6 h-5 w-8 rounded-sm border ${theme.line} bg-white/30`} />
            <div className={`absolute left-[45px] top-[31px] h-4 w-4 rounded ${theme.block}`} />
            <div className={`absolute left-4 top-[25px] h-1.5 w-1.5 rounded-full ${theme.node}`} />
            <div className={`absolute right-4 top-[25px] h-1.5 w-1.5 rounded-full ${theme.node}`} />
            <div className={`absolute left-4 bottom-5 h-1.5 w-1.5 rounded-full ${theme.node}`} />
            <div className={`absolute right-4 bottom-5 h-1.5 w-1.5 rounded-full ${theme.node}`} />
            <div className={`absolute bottom-5 left-5 right-5 h-px ${theme.line}`} />
        </div>
    );
}

export default FloorPlanLanding;
